import { memo, useMemo } from 'react';
import { ArrowStyleTypeKeys } from 'widgets/Lines/common/CommonLine.types';

import { getMarkerPath } from '../LineWidget.helpers';
import { DEFAULT_LINE_END_CONFIG, LINE_END_CONFIG } from './LineRender.config';

interface LineMarkerDefsProps {
  startArrowStyle: ArrowStyleTypeKeys;
  endArrowStyle: ArrowStyleTypeKeys;
  strokeColor: string;
  id?: string;
}

/**
 * LINE MARKER DEFINITIONS
 *
 * Component for generating marker definitions for line arrows in line widget.
 * Defines marker properties and paths based on the arrow style and stroke color.
 * Uses arrow configurations to create start and end arrow markers in SVG <defs>.
 *
 * NOTE: Should be usesd alongside <LineMarkers /> component. to render the actual line end
 *
 * @param startArrowStyle - Style for the starting arrow marker
 * @param endArrowStyle - Style for the ending arrow marker
 * @param strokeColor - Color of the marker's stroke
 * @param id - Optional ID for the marker (if provided)
 */
export const LineMarkerDefs = memo(({ id, startArrowStyle, endArrowStyle, strokeColor }: LineMarkerDefsProps) => {
  const startArrowMarkerPath = useMemo(() => {
    return getMarkerPath(startArrowStyle);
  }, [startArrowStyle]);

  const endArrowMarkerPath = useMemo(() => {
    return getMarkerPath(endArrowStyle);
  }, [endArrowStyle]);

  const startArrowConfig = useMemo(() => {
    const { markerProps, pathProps } = LINE_END_CONFIG[startArrowStyle] ?? DEFAULT_LINE_END_CONFIG;
    const newPathProps = { ...pathProps };
    if (pathProps.strokeWidth) {
      newPathProps.stroke = strokeColor;
      newPathProps.fill = 'transparent';
    } else {
      newPathProps.fill = strokeColor;
    }

    return { markerProps, pathProps: newPathProps };
  }, [startArrowStyle, strokeColor]);

  const endArrowConfig = useMemo(() => {
    const { markerProps, pathProps } = LINE_END_CONFIG[endArrowStyle] ?? DEFAULT_LINE_END_CONFIG;
    const newPathProps = { ...pathProps };
    if (pathProps.strokeWidth) {
      newPathProps.stroke = strokeColor;
      newPathProps.fill = 'transparent';
    } else {
      newPathProps.fill = strokeColor;
    }

    return { markerProps, pathProps: newPathProps };
  }, [endArrowStyle, strokeColor]);

  return (
    <defs>
      {/* Start arrow marker */}
      <marker id={`start-arrow-${id}`} orient='auto-start-reverse' {...startArrowConfig.markerProps}>
        {/* @ts-ignore - https://github.com/diegomura/react-pdf/issues/1873#issuecomment-1769426394 - strokeLinejoin shows warning in TS */}
        <path d={startArrowMarkerPath} strokeLinejoin='miter' {...startArrowConfig.pathProps}></path>
      </marker>

      {/* End arrow marker */}
      <marker id={`end-arrow-${id}`} orient='auto' {...endArrowConfig.markerProps}>
        {/* @ts-ignore - https://github.com/diegomura/react-pdf/issues/1873#issuecomment-1769426394 - strokeLinejoin shows warning in TS */}
        <path d={endArrowMarkerPath} strokeLinejoin='miter' {...endArrowConfig.pathProps}></path>
      </marker>
    </defs>
  );
});

LineMarkerDefs.displayName = 'LineMarkerDefs';

/**
 * LINE MARKERS
 *
 * Component responsible for rendering line markers with specified arrow styles and strokeWidth.
 * Renders two paths for start and end markers based on provided arrow styles and stroke width.
 *
 * Must be used with <LineMarkerDefs /> component to define the marker paths
 * @param startArrowStyle - Style for the starting arrow marker
 * @param endArrowStyle - Style for the ending arrow marker
 * @param strokeWidth - Width of the line stroke
 * @param path - Path for the line
 * @param id - Optional ID for the markers (if provided)
 */
interface LineMarkersProps {
  startArrowStyle: ArrowStyleTypeKeys;
  endArrowStyle: ArrowStyleTypeKeys;
  strokeWidth: number;
  path: string;
  id?: string;
}
export const LineMarkers = memo(({ startArrowStyle, endArrowStyle, strokeWidth, path, id }: LineMarkersProps) => {
  const startStrokeMultiplier = useMemo(() => {
    const startConfig = LINE_END_CONFIG[startArrowStyle];
    return startConfig?.strokeMultiplier ?? 1;
  }, [startArrowStyle]);

  const endStrokeMultiplier = useMemo(() => {
    const startConfig = LINE_END_CONFIG[endArrowStyle];
    return startConfig?.strokeMultiplier ?? 1;
  }, [endArrowStyle]);

  return (
    <>
      <path
        d={path}
        stroke='transparent'
        fill='transparent'
        strokeWidth={`${strokeWidth * endStrokeMultiplier}px`}
        markerEnd={`url(#end-arrow-${id})`}
        id={`end-arrow-path-${id}`}
      ></path>
      <path
        d={path}
        stroke='transparent'
        fill='transparent'
        strokeWidth={`${startStrokeMultiplier * strokeWidth}px`}
        markerStart={`url(#start-arrow-${id})`}
        id={`start-arrow-path-${id}`}
      ></path>
    </>
  );
});

LineMarkers.displayName = 'LineMarkers';
